import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useConfirm } from '../../hooks/useConfirm';
import {
  X, Mail, Phone, MapPin, Calendar, Paperclip, ChevronLeft,
  ChevronRight, FileText, Download, Eye, Tag, User, Briefcase,
  Edit2, Save, XCircle, Upload, Trash2, Bot, Send, Copy,
  DollarSign, Car, Clock, Star, Plus
} from 'lucide-react';
import { Candidate, CandidateDocument, candidatesService } from '../../services/candidates.service';
import { SubmissionModal } from '../recruiting/SubmissionModal';
import { stageInfo } from '../../constants/candidateStage';
import CandidateWorkflowHeader from './workflow/CandidateWorkflowHeader';
import CandidateActivityTimeline from './workflow/CandidateActivityTimeline';
import CandidateTaskList from './workflow/CandidateTaskList';
import KundenprozesseListe from './prozesse/KundenprozesseListe';
import '../../styles/modern-design.css';

interface CandidateDetailModalV2Props {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (updatedCandidate: Candidate) => void;
  onDelete?: (candidateId: string) => void;
  currentIndex?: number;
  totalCount?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

// Salutation options
const SALUTATION_OPTIONS = ['Herr', 'Frau', 'Divers', ''];

// Status und Talent-Status wurden zu einem Feld zusammengefuehrt:
// candidates.stage, definiert in constants/candidateStage.

// Availability options
const AVAILABILITY_OPTIONS = ['Sofort', '2 Wochen', '1 Monat', '3 Monate', '6 Monate', 'Nach Vereinbarung', ''];

// User type for "Bearbeitet von" dropdown
interface UserOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export const CandidateDetailModalV2: React.FC<CandidateDetailModalV2Props> = ({
  candidate,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  currentIndex = 0,
  totalCount = 1,
  onNavigate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  // Zähler, mit dem sich Kopf, Verlauf und Aufgaben gegenseitig auffrischen
  const [workflowZaehler, setWorkflowZaehler] = useState(0);
  const [editedCandidate, setEditedCandidate] = useState<Partial<Candidate>>({});
  const [documents, setDocuments] = useState<CandidateDocument[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [newSkill, setNewSkill] = useState('');
  const [newTag, setNewTag] = useState('');
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionRefreshKey, setSubmissionRefreshKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm, ConfirmDialogComponent } = useConfirm();

  // Load users for "Bearbeitet von" dropdown
  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      // Auth is handled automatically via httpOnly cookies
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/users`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();
      if (result.success && result.data) {
        setUsers(result.data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
    setLoadingUsers(false);
  };

  // Initialize edit state when candidate changes
  useEffect(() => {
    if (candidate) {
      setEditedCandidate({ ...candidate });
      loadDocuments(candidate.id);
    }
  }, [candidate]);

  // Load users when modal opens
  useEffect(() => {
    if (isOpen && users.length === 0) {
      loadUsers();
    }
  }, [isOpen, users.length]);

  useEffect(() => {
    let root = document.getElementById('modal-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'modal-root';
      document.body.appendChild(root);
    }
    setModalRoot(root);

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadDocuments = async (candidateId: string) => {
    setIsLoadingDocuments(true);
    try {
      const result = await candidatesService.getCandidateDocuments(candidateId);
      setDocuments(result.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
    setIsLoadingDocuments(false);
  };

  const handleSave = async () => {
    console.log('[CandidateModal] handleSave called, candidate:', candidate?.id);
    if (!candidate) return;
    setIsSaving(true);
    try {
      const result = await candidatesService.updateCandidate(candidate.id, editedCandidate);
      console.log('[CandidateModal] Update result:', result.success);
      if (result.success && onUpdate) {
        onUpdate(result.data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('[CandidateModal] Error saving candidate:', error);
      alert('Fehler beim Speichern: ' + (error as any)?.message);
    }
    setIsSaving(false);
  };

  const handleCancel = () => {
    if (candidate) {
      setEditedCandidate({ ...candidate });
    }
    setIsEditing(false);
  };

  // Handle close with unsaved changes warning
  const handleClose = async () => {
    if (isEditing) {
      const confirmed = await confirm({
        title: 'Ungespeicherte Änderungen',
        message: 'Sie haben ungespeicherte Änderungen. Möchten Sie wirklich schließen?',
        confirmText: 'Schließen',
        cancelText: 'Abbrechen',
        variant: 'warning',
      });
      if (!confirmed) return;
      handleCancel();
    }
    onClose();
  };

  const handleDelete = async () => {
    console.log('[CandidateModal] handleDelete called, candidate:', candidate?.id);
    if (!candidate) return;
    const confirmed = await confirm({
      title: 'Kandidat löschen',
      message: 'Möchten Sie diesen Kandidaten wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmText: 'Löschen',
      cancelText: 'Abbrechen',
      variant: 'danger',
    });
    console.log('[CandidateModal] Delete confirmed:', confirmed);
    if (!confirmed) return;
    try {
      await candidatesService.deleteCandidate(candidate.id);
      if (onDelete) {
        onDelete(candidate.id);
      }
      onClose();
    } catch (error) {
      console.error('[CandidateModal] Error deleting candidate:', error);
      alert('Fehler beim Löschen des Kandidaten: ' + (error as any)?.message);
    }
  };

  const handleAnalyze = async () => {
    if (!candidate) return;
    setIsAnalyzing(true);
    try {
      const result = await candidatesService.analyzeCandidatePDF(candidate.id);
      if (result.success && result.data) {
        // Reload candidate data after analysis
        const updatedCandidate = await candidatesService.getCandidate(candidate.id);
        if (updatedCandidate.success && onUpdate) {
          onUpdate(updatedCandidate.data);
        }
      }
    } catch (error) {
      console.error('Error analyzing PDF:', error);
    }
    setIsAnalyzing(false);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !candidate) return;

    try {
      await candidatesService.uploadCandidateDocument(candidate.id, file);
      await loadDocuments(candidate.id);
    } catch (error) {
      console.error('Error uploading file:', error);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    const confirmed = await confirm({
      title: 'Dokument löschen',
      message: 'Möchten Sie dieses Dokument wirklich löschen?',
      confirmText: 'Löschen',
      cancelText: 'Abbrechen',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await candidatesService.deleteCandidateDocument(documentId);
      setDocuments(docs => docs.filter(d => d.id !== documentId));
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleAddSkill = () => {
    if (!newSkill.trim()) return;
    const currentSkills = editedCandidate.skills || [];
    if (!currentSkills.includes(newSkill.trim())) {
      setEditedCandidate(prev => ({
        ...prev,
        skills: [...currentSkills, newSkill.trim()]
      }));
    }
    setNewSkill('');
  };

  const handleRemoveSkill = (skill: string) => {
    setEditedCandidate(prev => ({
      ...prev,
      skills: (prev.skills || []).filter(s => s !== skill)
    }));
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    const currentTags = editedCandidate.tags || [];
    if (!currentTags.includes(newTag.trim())) {
      setEditedCandidate(prev => ({
        ...prev,
        tags: [...currentTags, newTag.trim()]
      }));
    }
    setNewTag('');
  };

  const handleRemoveTag = (tag: string) => {
    setEditedCandidate(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(t => t !== tag)
    }));
  };

  const updateField = (field: keyof Candidate, value: any) => {
    setEditedCandidate(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (stage?: string | null) => stageInfo(stage).color;

  const formatAddress = () => {
    const parts = [
      editedCandidate.street,
      [editedCandidate.postal_code, editedCandidate.city].filter(Boolean).join(' '),
      editedCandidate.country
    ].filter(Boolean);
    return parts.join('\n');
  };

  const getDisplayName = () => {
    const salutation = editedCandidate.salutation || '';
    const firstName = editedCandidate.first_name || '';
    const lastName = editedCandidate.last_name || '';
    return [salutation, firstName, lastName].filter(Boolean).join(' ');
  };

  if (!candidate || !modalRoot) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="candidate-detail-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '1rem'
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-lg)',
              width: '100%',
              maxWidth: '1200px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--glass-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--glass-subtle)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button onClick={handleClose} className="btn btn-ghost" style={{ padding: '0.5rem' }}>
                  <ChevronLeft size={20} />
                  <span style={{ marginLeft: '0.25rem' }}>Zurück</span>
                </button>

                {/* PHE ID Badge */}
                <div style={{
                  background: 'var(--accent-primary)',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace'
                }}>
                  {editedCandidate.candidate_number || 'Keine ID'}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {/* Navigation */}
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  {currentIndex + 1} / {totalCount}
                </span>
                <button
                  onClick={() => onNavigate?.('prev')}
                  disabled={currentIndex === 0}
                  className="btn btn-ghost"
                  style={{ padding: '0.5rem', opacity: currentIndex === 0 ? 0.5 : 1 }}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => onNavigate?.('next')}
                  disabled={currentIndex === totalCount - 1}
                  className="btn btn-ghost"
                  style={{ padding: '0.5rem', opacity: currentIndex === totalCount - 1 ? 0.5 : 1 }}
                >
                  <ChevronRight size={18} />
                </button>

                {/* Edit Toggle */}
                {isEditing ? (
                  <>
                    <button onClick={handleCancel} className="btn btn-ghost" style={{ color: 'var(--text-secondary)' }}>
                      <XCircle size={18} />
                      <span style={{ marginLeft: '0.25rem' }}>Abbrechen</span>
                    </button>
                    <button onClick={handleSave} className="btn btn-primary" disabled={isSaving}>
                      <Save size={18} />
                      <span style={{ marginLeft: '0.25rem' }}>{isSaving ? 'Speichern...' : 'Speichern'}</span>
                    </button>
                  </>
                ) : (
                  <button onClick={() => setIsEditing(true)} className="btn btn-secondary">
                    <Edit2 size={18} />
                    <span style={{ marginLeft: '0.25rem' }}>Bearbeiten</span>
                  </button>
                )}

                <button onClick={onClose} className="btn btn-ghost" style={{ padding: '0.5rem' }}>
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
              {/* Candidate Header */}
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '2rem' }}>
                {/* Avatar */}
                <div style={{
                  width: 100,
                  height: 100,
                  borderRadius: 'var(--radius-full)',
                  background: `linear-gradient(135deg, ${getStatusColor(editedCandidate.stage)}, ${getStatusColor(editedCandidate.stage)}99)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  color: 'white',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {(editedCandidate.first_name?.[0] || '') + (editedCandidate.last_name?.[0] || '')}
                </div>

                {/* Name & Contact */}
                <div style={{ flex: 1 }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                          value={editedCandidate.salutation || ''}
                          onChange={(e) => updateField('salutation', e.target.value || null)}
                          className="input-field"
                          style={{ width: '100px' }}
                        >
                          {SALUTATION_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt || '-- Anrede --'}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={editedCandidate.first_name || ''}
                          onChange={(e) => updateField('first_name', e.target.value)}
                          className="input-field"
                          placeholder="Vorname"
                          style={{ flex: 1 }}
                        />
                        <input
                          type="text"
                          value={editedCandidate.last_name || ''}
                          onChange={(e) => updateField('last_name', e.target.value)}
                          className="input-field"
                          placeholder="Nachname"
                          style={{ flex: 1 }}
                        />
                      </div>
                      <input
                        type="text"
                        value={editedCandidate.current_position || ''}
                        onChange={(e) => updateField('current_position', e.target.value)}
                        className="input-field"
                        placeholder="Position / Rolle"
                      />
                    </div>
                  ) : (
                    <>
                      <h2 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                        {getDisplayName() || 'Unbekannt'}
                      </h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
                        {editedCandidate.current_position || 'Keine Position angegeben'}
                      </p>
                    </>
                  )}

                  {/* Contact Info */}
                  <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                    {isEditing ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Mail size={16} style={{ color: 'var(--text-tertiary)' }} />
                          <input
                            type="email"
                            value={editedCandidate.email || ''}
                            onChange={(e) => updateField('email', e.target.value)}
                            className="input-field"
                            placeholder="E-Mail"
                            style={{ width: '200px' }}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Phone size={16} style={{ color: 'var(--text-tertiary)' }} />
                          <input
                            type="tel"
                            value={editedCandidate.phone || ''}
                            onChange={(e) => updateField('phone', e.target.value)}
                            className="input-field"
                            placeholder="Telefon"
                            style={{ width: '150px' }}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {editedCandidate.email && (
                          <a href={`mailto:${editedCandidate.email}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            color: 'var(--accent-primary)', textDecoration: 'none'
                          }}>
                            <Mail size={16} /> {editedCandidate.email}
                          </a>
                        )}
                        {editedCandidate.phone && (
                          <a href={`tel:${editedCandidate.phone}`} style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            color: 'var(--accent-primary)', textDecoration: 'none'
                          }}>
                            <Phone size={16} /> {editedCandidate.phone}
                          </a>
                        )}
                      </>
                    )}
                  </div>

                  {/* Arbeitsstand: Status, Priorität, Zuständigkeit, letzte
                      Aktivität, nächste Aufgabe (Schritt 2, Regel 28) */}
                  {candidate?.id && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <CandidateWorkflowHeader
                        candidateId={candidate.id}
                        aktualisierungsZaehler={workflowZaehler}
                        onGeaendert={() => setWorkflowZaehler((z) => z + 1)}
                      />
                    </div>
                  )}

                  <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', rowGap: '0.5rem' }}>

                    {/* Erstelldatum */}
                    {candidate?.created_at && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} style={{ color: 'var(--text-tertiary)' }} />
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>
                          Erstellt: {new Date(candidate.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </span>
                      </div>
                    )}

                    {/* Bearbeitet von */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <User size={14} style={{ color: 'var(--text-tertiary)' }} />
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>Bearbeitet von:</span>
                      {isEditing ? (
                        <select
                          value={!(editedCandidate as any).edited_by && ((editedCandidate as any).tags || []).includes('recruiting_pool') ? 'recruiting_pool' : ((editedCandidate as any).edited_by || '')}
                          onChange={(e) => {
                            if (e.target.value === 'recruiting_pool') {
                              updateField('edited_by', null);
                              const currentTags = (editedCandidate as any).tags || [];
                              if (!currentTags.includes('recruiting_pool')) {
                                updateField('tags', [...currentTags, 'recruiting_pool']);
                              }
                            } else {
                              updateField('edited_by', e.target.value || null);
                              const currentTags = (editedCandidate as any).tags || [];
                              updateField('tags', currentTags.filter((t: string) => t !== 'recruiting_pool'));
                            }
                          }}
                          disabled={loadingUsers}
                          style={{
                            background: 'var(--glass)',
                            border: '1px solid var(--glass-border)',
                            borderRadius: 'var(--radius-md)',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.875rem',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            minWidth: '150px'
                          }}
                        >
                          <option value="">Nicht bekannt</option>
                          <option value="recruiting_pool">Recruiting Pool</option>
                          {users.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span style={{
                          fontSize: '0.875rem',
                          color: ((editedCandidate as any).edited_by || ((editedCandidate as any).tags || []).includes('recruiting_pool')) ? 'var(--text-primary)' : 'var(--text-tertiary)',
                          fontStyle: ((editedCandidate as any).edited_by || ((editedCandidate as any).tags || []).includes('recruiting_pool')) ? 'normal' : 'italic'
                        }}>
                          {!(editedCandidate as any).edited_by && ((editedCandidate as any).tags || []).includes('recruiting_pool')
                            ? 'Recruiting Pool'
                            : (editedCandidate as any).edited_by
                              ? users.find(u => u.id === (editedCandidate as any).edited_by)
                                ? `${users.find(u => u.id === (editedCandidate as any).edited_by)?.first_name} ${users.find(u => u.id === (editedCandidate as any).edited_by)?.last_name}`
                                : 'Laden...'
                              : 'Nicht bekannt'
                          }
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Two Column Layout */}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1.5rem' }}>
                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: 0, overflow: 'hidden' }}>
                  {/* Address Section */}
                  <Section title="Adresse" icon={<MapPin size={18} />}>
                    {isEditing ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <input
                          type="text"
                          value={editedCandidate.street || ''}
                          onChange={(e) => updateField('street', e.target.value)}
                          className="input-field"
                          placeholder="Straße und Hausnummer"
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            type="text"
                            value={editedCandidate.postal_code || ''}
                            onChange={(e) => updateField('postal_code', e.target.value)}
                            className="input-field"
                            placeholder="PLZ"
                            style={{ width: '100px' }}
                          />
                          <input
                            type="text"
                            value={editedCandidate.city || ''}
                            onChange={(e) => updateField('city', e.target.value)}
                            className="input-field"
                            placeholder="Stadt"
                            style={{ flex: 1 }}
                          />
                        </div>
                        <input
                          type="text"
                          value={editedCandidate.country || ''}
                          onChange={(e) => updateField('country', e.target.value)}
                          className="input-field"
                          placeholder="Land"
                        />
                      </div>
                    ) : (
                      <p style={{ whiteSpace: 'pre-line', color: 'var(--text-secondary)' }}>
                        {formatAddress() || 'Keine Adresse hinterlegt'}
                      </p>
                    )}
                  </Section>

                  {/* Description Section */}
                  <Section title="Beschreibung / Interne Informationen" icon={<FileText size={18} />}>
                    {isEditing ? (
                      <textarea
                        value={editedCandidate.description || ''}
                        onChange={(e) => updateField('description', e.target.value)}
                        className="input-field"
                        placeholder="Beschreibung oder interne Informationen..."
                        rows={5}
                        style={{ resize: 'vertical' }}
                      />
                    ) : (
                      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        {editedCandidate.description || 'Keine Beschreibung vorhanden'}
                      </p>
                    )}
                  </Section>

                  {/* Skills Section */}
                  <Section title="Skills & Qualifikationen" icon={<Star size={18} />}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {(editedCandidate.skills || []).map((skill, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'var(--accent-primary)',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: 'var(--radius-full)',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          {skill}
                          {isEditing && (
                            <button
                              onClick={() => handleRemoveSkill(skill)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <X size={14} color="white" />
                            </button>
                          )}
                        </span>
                      ))}
                      {(editedCandidate.skills || []).length === 0 && !isEditing && (
                        <span style={{ color: 'var(--text-tertiary)' }}>Keine Skills hinterlegt</span>
                      )}
                    </div>
                    {isEditing && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <input
                          type="text"
                          value={newSkill}
                          onChange={(e) => setNewSkill(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                          className="input-field"
                          placeholder="Neuen Skill hinzufügen..."
                          style={{ flex: 1 }}
                        />
                        <button onClick={handleAddSkill} className="btn btn-primary" style={{ padding: '0.5rem' }}>
                          <Plus size={18} />
                        </button>
                      </div>
                    )}
                  </Section>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: 0, overflow: 'hidden' }}>
                  {/* Submissions Section - prominent at top */}
                  <Section
                    title="Kundenprozesse"
                    icon={<Send size={18} />}
                    action={
                      <button
                        onClick={() => setShowSubmissionModal(true)}
                        className="btn btn-primary"
                        style={{
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.8125rem',
                          height: '32px',
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          border: 'none',
                          color: '#fff',
                          borderRadius: 'var(--radius-md)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          cursor: 'pointer',
                          fontWeight: 600
                        }}
                      >
                        <Plus size={14} />
                        Bei Kunde vorstellen
                      </button>
                    }
                  >
                    {candidate && (
                      <KundenprozesseListe
                        candidateId={candidate.id}
                        aktualisierungsZaehler={submissionRefreshKey + workflowZaehler}
                        onGeaendert={() => setWorkflowZaehler((z) => z + 1)}
                      />
                    )}
                  </Section>

                  {/* Details Section */}
                  <Section title="Details" icon={<Briefcase size={18} />}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1rem' }}>
                      <DetailField
                        label="Verfügbarkeit"
                        value={editedCandidate.availability}
                        isEditing={isEditing}
                        type="select"
                        options={AVAILABILITY_OPTIONS}
                        onChange={(v) => updateField('availability', v)}
                      />
                      <DetailField
                        label="Berufserfahrung"
                        value={editedCandidate.years_experience ? `${editedCandidate.years_experience} Jahre` : null}
                        isEditing={isEditing}
                        type="number"
                        onChange={(v) => updateField('years_experience', v ? parseInt(v) : null)}
                        suffix="Jahre"
                      />
                      <DetailField
                        label="Gehaltsvorstellung"
                        value={editedCandidate.salary_expectation}
                        isEditing={isEditing}
                        onChange={(v) => updateField('salary_expectation', v)}
                        icon={<DollarSign size={14} />}
                      />
                      <DetailField
                        label="Reisebereitschaft"
                        value={editedCandidate.travel_willingness}
                        isEditing={isEditing}
                        onChange={(v) => updateField('travel_willingness', v)}
                        icon={<Car size={14} />}
                      />
                      <DetailField
                        label="Schichtbereitschaft"
                        value={editedCandidate.shift_readiness}
                        isEditing={isEditing}
                        onChange={(v) => updateField('shift_readiness', v)}
                        icon={<Clock size={14} />}
                      />
                    </div>
                  </Section>

                  {/* Documents Section */}
                  {/* Schritt 2: Verlauf und Aufgaben als eigene Komponenten */}
                  {candidate?.id && (
                    <Section title="Aufgaben" icon={<Calendar size={18} />}>
                      <CandidateTaskList
                        candidateId={candidate.id}
                        aktualisierungsZaehler={workflowZaehler}
                        onGeaendert={() => setWorkflowZaehler((z) => z + 1)}
                      />
                    </Section>
                  )}

                  {candidate?.id && (
                    <Section title="Verlauf" icon={<FileText size={18} />}>
                      <CandidateActivityTimeline
                        candidateId={candidate.id}
                        aktualisierungsZaehler={workflowZaehler}
                        onGeaendert={() => setWorkflowZaehler((z) => z + 1)}
                      />
                    </Section>
                  )}

                  <Section title="Dokumente" icon={<Paperclip size={18} />} action={
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="btn btn-ghost"
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                    >
                      <Upload size={14} />
                      <span style={{ marginLeft: '0.25rem' }}>Upload</span>
                    </button>
                  }>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    />
                    {isLoadingDocuments ? (
                      <p style={{ color: 'var(--text-tertiary)' }}>Lade Dokumente...</p>
                    ) : documents.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {documents.map((doc) => (
                          <div
                            key={doc.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.5rem',
                              background: 'var(--glass)',
                              borderRadius: 'var(--radius-md)',
                              border: '1px solid var(--glass-border)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <Paperclip size={16} style={{ color: 'var(--accent-primary)' }} />
                              <span style={{ fontSize: '0.875rem' }}>{doc.name}</span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                {(doc.file_size / 1024 / 1024).toFixed(1)} MB
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              <a
                                href={candidatesService.getDocumentDownloadUrl(doc.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-ghost"
                                style={{ padding: '0.25rem' }}
                              >
                                <Eye size={16} />
                              </a>
                              <a
                                href={candidatesService.getDocumentDownloadUrl(doc.id)}
                                download={doc.name}
                                className="btn btn-ghost"
                                style={{ padding: '0.25rem' }}
                              >
                                <Download size={16} />
                              </a>
                              <button
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="btn btn-ghost"
                                style={{ padding: '0.25rem', color: 'var(--accent-error)' }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-tertiary)' }}>Keine Dokumente vorhanden</p>
                    )}
                  </Section>

                  {/* Tags Section */}
                  <Section title="Tags" icon={<Tag size={18} />}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {(editedCandidate.tags || []).filter(t => t !== 'recruiting_pool').map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            background: 'var(--glass)',
                            border: '1px solid var(--glass-border)',
                            padding: '0.25rem 0.75rem',
                            borderRadius: 'var(--radius-full)',
                            fontSize: '0.875rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <Tag size={12} />
                          {tag}
                          {isEditing && (
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </span>
                      ))}
                      {(editedCandidate.tags || []).filter(t => t !== 'recruiting_pool').length === 0 && !isEditing && (
                        <span style={{ color: 'var(--text-tertiary)' }}>Keine Tags</span>
                      )}
                    </div>
                    {isEditing && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                          className="input-field"
                          placeholder="Neuen Tag hinzufügen..."
                          style={{ flex: 1 }}
                        />
                        <button onClick={handleAddTag} className="btn btn-secondary" style={{ padding: '0.5rem' }}>
                          <Plus size={18} />
                        </button>
                      </div>
                    )}
                  </Section>

                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '0.75rem 1.5rem',
              borderTop: '1px solid var(--glass-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--glass-subtle)',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing || documents.length === 0}
                  className="btn btn-secondary"
                  style={{
                    opacity: documents.length === 0 ? 0.5 : 1,
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.8125rem',
                    height: '32px'
                  }}
                >
                  <Bot size={14} />
                  <span style={{ marginLeft: '0.25rem' }}>{isAnalyzing ? 'Analysiere...' : 'KI-Analyse'}</span>
                </button>
                <button
                  onClick={() => setShowSubmissionModal(true)}
                  className="btn btn-secondary"
                  style={{
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.8125rem',
                    height: '32px',
                    background: 'rgba(99,102,241,0.1)',
                    color: '#6366f1',
                    border: '1px solid rgba(99,102,241,0.2)'
                  }}
                >
                  <Briefcase size={14} />
                  <span style={{ marginLeft: '0.25rem' }}>Bei Kunde vorstellen</span>
                </button>
                <button
                  onClick={() => window.location.href = `mailto:${editedCandidate.email}`}
                  disabled={!editedCandidate.email}
                  className="btn btn-secondary"
                  style={{
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.8125rem',
                    height: '32px'
                  }}
                >
                  <Send size={14} />
                  <span style={{ marginLeft: '0.25rem' }}>E-Mail</span>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${editedCandidate.first_name} ${editedCandidate.last_name} - ${editedCandidate.email || ''} - ${editedCandidate.phone || ''}`);
                  }}
                  className="btn btn-ghost"
                  style={{
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.8125rem',
                    height: '32px'
                  }}
                >
                  <Copy size={14} />
                  <span style={{ marginLeft: '0.25rem' }}>Kopieren</span>
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-ghost"
                  style={{
                    color: 'var(--accent-error)',
                    padding: '0.375rem 0.625rem',
                    fontSize: '0.8125rem',
                    height: '32px'
                  }}
                >
                  <Trash2 size={14} />
                  <span style={{ marginLeft: '0.25rem' }}>Löschen</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {createPortal(modalContent, modalRoot)}
      {createPortal(ConfirmDialogComponent, document.body)}
      {candidate && (
        <SubmissionModal
          isOpen={showSubmissionModal}
          onClose={() => setShowSubmissionModal(false)}
          candidateId={candidate.id}
          candidateName={`${candidate.first_name || ''} ${candidate.last_name || ''}`.trim()}
          onSuccess={() => {
            // Force re-render of timeline
            setSubmissionRefreshKey(k => k + 1);
          }}
        />
      )}
    </>
  );
};

// Helper Components
const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, icon, children, action }) => (
  <div style={{
    background: 'var(--glass)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-md)',
    padding: '1rem',
    minWidth: 0,
    overflow: 'hidden'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.75rem'
    }}>
      <h3 style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: 'var(--text-primary)'
      }}>
        {icon}
        {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

const DetailField: React.FC<{
  label: string;
  value: string | number | null | undefined;
  isEditing: boolean;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'select';
  options?: string[] | Array<{ value: string; label: string }>;
  icon?: React.ReactNode;
  suffix?: string;
}> = ({ label, value, isEditing, onChange, type = 'text', options, icon, suffix }) => {
  // Helper to get display value for select fields
  const getDisplayValue = () => {
    if (type === 'select' && options && options.length > 0) {
      // Check if options are objects with value/label
      if (typeof options[0] === 'object' && 'value' in options[0]) {
        const optionsAsObjects = options as Array<{ value: string; label: string }>;
        const found = optionsAsObjects.find(opt => opt.value === value);
        return found ? found.label : (value || '-');
      }
    }
    return value || '-';
  };

  return (
    <div>
      <label style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', display: 'block', marginBottom: '0.25rem' }}>
        {label}
      </label>
      {isEditing ? (
        type === 'select' && options ? (
          <select
            value={value?.toString() || ''}
            onChange={(e) => onChange(e.target.value)}
            className="input-field"
            style={{ width: '100%' }}
          >
            {options.map(opt => {
              // Handle both string[] and {value, label}[] formats
              if (typeof opt === 'object' && 'value' in opt) {
                return <option key={opt.value} value={opt.value}>{opt.label || `-- ${label} --`}</option>;
              } else {
                return <option key={opt} value={opt}>{opt || `-- ${label} --`}</option>;
              }
            })}
          </select>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <input
              type={type}
              value={type === 'number' ? (value?.toString().replace(' Jahre', '') || '') : (value?.toString() || '')}
              onChange={(e) => onChange(e.target.value)}
              className="input-field"
              style={{ flex: 1 }}
            />
            {suffix && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.875rem' }}>{suffix}</span>}
          </div>
        )
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-secondary)' }}>
          {icon}
          <span>{getDisplayValue()}</span>
        </div>
      )}
    </div>
  );
};

export default CandidateDetailModalV2;
